'use client';

import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { usePersistedState } from '@/lib/usePersistedState';

const PLATFORMS = ['LinkedIn', 'Instagram', 'X', 'TikTok'] as const;
type Platform = typeof PLATFORMS[number];

const PLATFORM_COLORS: Record<Platform, string> = {
  LinkedIn: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  Instagram: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  X: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  TikTok: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

const PLATFORM_ICONS: Record<Platform, string> = { LinkedIn: 'in', Instagram: 'IG', X: 'X', TikTok: 'TT' };
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface SocialPost { id: string; day: number; platform: Platform; title: string; time?: string; }

function getWeekDates(offset: number): Date[] {
  const now = new Date();
  const dow = now.getDay();
  const mon = new Date(now);
  mon.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1) + offset * 7);
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return d; });
}

export default function SocialPage() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [posts, setPosts] = usePersistedState<SocialPost[]>('content:social-posts', []);
  const [addingTo, setAddingTo] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [platform, setPlatform] = useState<Platform>('LinkedIn');
  const [time, setTime] = useState('09:00');

  const weekDates = useMemo(() => getWeekDates(weekOffset), [weekOffset]);
  const weekLabel = `${weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const addPost = () => {
    if (!title.trim() || addingTo === null) return;
    setPosts(prev => [...prev, { id: crypto.randomUUID(), day: addingTo, platform, title: title.trim(), time }]);
    setTitle(''); setAddingTo(null);
  };

  const removePost = (id: string) => setPosts(prev => prev.filter(p => p.id !== id));

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-xl font-semibold text-white">Social Calendar</h1>
        <p className="text-sm text-gray-500 mt-1">Plan posts across platforms</p>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={() => setWeekOffset(w => w - 1)} className="p-2 rounded-lg bg-white/[0.04] hover:bg-gray-800 text-gray-400"><ChevronLeft className="w-4 h-4" /></button>
        <div className="text-center">
          <p className="text-sm font-medium text-white">{weekLabel}</p>
          {weekOffset !== 0 && <button onClick={() => setWeekOffset(0)} className="text-xs text-cyan-400 hover:underline">Today</button>}
        </div>
        <button onClick={() => setWeekOffset(w => w + 1)} className="p-2 rounded-lg bg-white/[0.04] hover:bg-gray-800 text-gray-400"><ChevronRight className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-7 gap-2">
        {DAYS.map((day, i) => {
          const date = weekDates[i];
          const isToday = new Date().toDateString() === date.toDateString();
          const dayPosts = posts.filter(p => p.day === i);
          return (
            <div key={day} className={`rounded-xl border p-3 min-h-[200px] flex flex-col ${isToday ? 'border-cyan-500/40 bg-cyan-500/5' : 'border-white/[0.08] bg-white/[0.04]'}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className={`text-xs font-bold ${isToday ? 'text-cyan-400' : 'text-gray-400'}`}>{day}</p>
                  <p className="text-[10px] text-gray-600">{date.getDate()}</p>
                </div>
                <button onClick={() => setAddingTo(addingTo === i ? null : i)} className="p-1 rounded hover:bg-white/10 text-gray-600 hover:text-cyan-400"><Plus className="h-3.5 w-3.5" /></button>
              </div>
              {addingTo === i && (
                <div className="mb-2 space-y-1.5 p-2 rounded-lg bg-[rgb(var(--background))]/50 border border-cyan-500/20">
                  <input type="text" value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && addPost()}
                    placeholder="Post title..." className="w-full px-2 py-1 text-xs rounded bg-gray-900 border border-white/[0.08] text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/50" autoFocus />
                  <div className="flex gap-1">
                    {PLATFORMS.map(p => (
                      <button key={p} onClick={() => setPlatform(p)}
                        className={`text-[10px] px-1.5 py-0.5 rounded border ${platform === p ? PLATFORM_COLORS[p] : 'border-transparent text-gray-600'}`}>
                        {PLATFORM_ICONS[p]}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-1.5">
                    <input type="time" value={time} onChange={e => setTime(e.target.value)} className="flex-1 px-2 py-0.5 text-[10px] rounded bg-gray-900 border border-white/[0.08] text-gray-400 focus:outline-none" />
                    <button onClick={addPost} className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-[10px] font-bold">Add</button>
                  </div>
                </div>
              )}
              <div className="flex-1 space-y-1.5">
                {dayPosts.map(post => (
                  <div key={post.id} className={`px-2 py-1.5 rounded-lg border text-xs group ${PLATFORM_COLORS[post.platform]}`}>
                    <div className="flex items-start justify-between gap-1">
                      <div><span className="font-bold mr-1">{PLATFORM_ICONS[post.platform]}</span><span className="text-[11px]">{post.title}</span></div>
                      <button onClick={() => removePost(post.id)} className="opacity-0 group-hover:opacity-100 shrink-0"><X className="h-3 w-3" /></button>
                    </div>
                    {post.time && <p className="text-[10px] opacity-60 mt-0.5">{post.time}</p>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4 justify-center">
        {PLATFORMS.map(p => (
          <div key={p} className="flex items-center gap-1.5">
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PLATFORM_COLORS[p]}`}>{PLATFORM_ICONS[p]}</span>
            <span className="text-xs text-gray-500">{p}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
